import { Circle, FabricText, Group, Line, Path, Polygon, Rect } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { FillValue, ToolRecipe } from '../types';

/**
 * Math manipulatives — classroom-poster quality: chunky strokes, round caps,
 * vibrant fills, labels sized to read from the back row.
 *
 * Every generator builds children in ABSOLUTE coordinates (whatever origin is
 * convenient), then groups them and re-anchors the group at 0,0 — fabric's
 * Group constructor computes the bounding box, so interior offsets never leak
 * into placement. Params arrive untrusted (LLM- or user-typed), so every read
 * goes through a coercing helper and generate() never throws.
 */

const TAU = Math.PI * 2;

/** Cycling accent palette for hops / plotted points / timeline dots. */
const ACCENTS = ['#e11d48', '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2'];

const INK = '#1e293b';

// ---------------------------------------------------------------------------
// Defensive param coercion — params may be numbers-as-strings, JSON strings,
// or missing entirely; recipes must render something sensible regardless.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

/** json params may arrive as a JSON string or an already-parsed array. */
function jsonArray<T>(v: unknown, fallback: T[]): T[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return fallback;
    }
  }
  return Array.isArray(parsed) ? (parsed as T[]) : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Print a value without float noise (0.30000000000000004 → "0.3"). */
function fmt(v: number): string {
  return String(Math.round(v * 1000) / 1000);
}

// ---------------------------------------------------------------------------
// Geometry + primitive helpers
// ---------------------------------------------------------------------------

/** Clock convention: angle 0 at 12 o'clock, increasing clockwise. */
function polar(cx: number, cy: number, r: number, angle: number): { x: number; y: number } {
  return { x: cx + r * Math.sin(angle), y: cy - r * Math.cos(angle) };
}

function strokeLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  strokeWidth: number,
): Line {
  return new Line([x1, y1, x2, y2], { stroke, strokeWidth, strokeLineCap: 'round' });
}

/**
 * Chunky arrowhead anchored at its tip. `angle` is screen-math radians
 * (0 = pointing +x, PI/2 = pointing down); the shallow tail notch keeps the
 * silhouette friendly rather than dagger-like.
 */
function arrowHead(tipX: number, tipY: number, angle: number, size: number, fill: string): Polygon {
  const local = [
    { x: 0, y: 0 },
    { x: -1.7 * size, y: -size },
    { x: -1.3 * size, y: 0 },
    { x: -1.7 * size, y: size },
  ];
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const points = local.map((p) => ({
    x: tipX + p.x * cos - p.y * sin,
    y: tipY + p.x * sin + p.y * cos,
  }));
  return new Polygon(points, { fill, stroke: fill, strokeWidth: 2, strokeLineJoin: 'round' });
}

interface TextSpec {
  x: number;
  y: number;
  size: number;
  font: string;
  fill?: string;
  weight?: string | number;
  originX?: 'left' | 'center' | 'right';
  originY?: 'top' | 'center' | 'bottom';
}

function makeText(content: string, o: TextSpec): FabricText {
  return new FabricText(content, {
    left: o.x,
    top: o.y,
    fontSize: o.size,
    fontFamily: o.font,
    fontWeight: o.weight ?? 'bold',
    fill: o.fill ?? INK,
    originX: o.originX ?? 'center',
    originY: o.originY ?? 'center',
  });
}

function finish(
  recipe: Pick<ToolRecipe, 'id' | 'version'>,
  params: Record<string, unknown>,
  children: FabricObject[],
): Group {
  const group = new Group(children);
  group.set({ left: 0, top: 0 });
  return tagSmartObject(group, recipe, params);
}

// ---------------------------------------------------------------------------
// 1. number-line
// ---------------------------------------------------------------------------

interface HopSpec {
  from?: unknown;
  to?: unknown;
  color?: unknown;
}

const numberLine: ToolRecipe = {
  id: 'number-line',
  domain: 'math',
  tags: ['number-line', 'counting', 'addition', 'skip-counting', 'model', 'K-2'],
  featured: true,
  name: 'Number Line',
  category: 'math',
  version: 1,
  description:
    'Number line with arrowheads, major/minor ticks, numbers, and optional arced jump arrows for skip counting or addition hops.',
  defaults: {
    min: 0,
    max: 10,
    step: 1,
    width: 900,
    lineColor: INK,
    fontFamily: 'Arial',
    labelFontSize: 30,
    hops: [],
  },
  schema: [
    { key: 'min', label: 'Start value', type: 'number', step: 1 },
    { key: 'max', label: 'End value', type: 'number', step: 1 },
    { key: 'step', label: 'Step between ticks', type: 'number', min: 0, step: 0.5 },
    {
      key: 'hops',
      label: 'Jump arrows',
      type: 'list',
      help: 'Arcs drawn above the line for skip counting or addition hops.',
      summaryKeys: ['from', 'to'],
      minItems: 0,
      maxItems: 12,
      itemSchema: [
        { key: 'from', label: 'Jump from', type: 'number', step: 0.5 },
        { key: 'to', label: 'Jump to', type: 'number', step: 0.5 },
        {
          key: 'color',
          label: 'Arrow color',
          type: 'color',
          help: 'Leave unset to auto-pick an accent color.',
        },
      ],
    },
    { key: 'lineColor', label: 'Line color', type: 'color' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'width',
      label: 'Width (px)',
      type: 'number',
      min: 300,
      max: 3000,
      step: 10,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Number size',
      type: 'number',
      min: 12,
      max: 80,
      step: 1,
      advanced: true,
    },
  ],
  async generate(params) {
    const rawMin = num(params.min, 0);
    const rawMax = num(params.max, 10);
    const min = Math.min(rawMin, rawMax);
    const max = rawMax > rawMin ? rawMax : min + 10;
    const span = max - min;
    // Degenerate params must not explode the object graph — coarsen the step
    // until the line holds a poster-readable number of ticks.
    let step = Math.abs(num(params.step, 1)) || 1;
    while (span / step > 60) step *= 2;
    const width = clamp(num(params.width, 900), 300, 4000);
    const lineColor = str(params.lineColor, INK);
    const font = str(params.fontFamily, 'Arial');
    const numberSize = clamp(num(params.labelFontSize, 30), 10, 120);

    const padX = 60;
    const usable = width - padX * 2;
    const y = 200; // headroom above for hop arcs; group bbox trims the rest
    const xOf = (v: number) => padX + ((v - min) / span) * usable;

    const children: FabricObject[] = [];

    // Main line, slightly overhanging the end ticks, arrowheads both ways.
    const overhang = 26;
    const lx1 = xOf(min) - overhang;
    const lx2 = xOf(max) + overhang;
    children.push(strokeLine(lx1, y, lx2, y, lineColor, 7));
    children.push(arrowHead(lx1 - 20, y, Math.PI, 15, lineColor));
    children.push(arrowHead(lx2 + 20, y, 0, 15, lineColor));

    // Major ticks with numbers; minor half-step ticks only when they have room.
    const majorGapPx = (step / span) * usable;
    for (let v = min; v <= max + 1e-9; v += step) {
      const x = xOf(v);
      children.push(strokeLine(x, y - 19, x, y + 19, lineColor, 5));
      children.push(
        makeText(fmt(v), { x, y: y + 34, size: numberSize, font, fill: lineColor, originY: 'top' }),
      );
      if (majorGapPx >= 44 && v + step <= max + 1e-9) {
        const mx = xOf(v + step / 2);
        children.push(strokeLine(mx, y - 11, mx, y + 11, lineColor, 3));
      }
    }

    // Hop arcs: quadratic curves that leave and land just above the ticks,
    // arrowhead on the landing end, jump size labelled at the apex.
    const hops = jsonArray<HopSpec>(params.hops, []);
    hops.forEach((hop, i) => {
      if (!hop || typeof hop !== 'object') return;
      const from = num(hop.from, NaN);
      const to = num(hop.to, NaN);
      if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return;
      const f = clamp(from, min, max);
      const t = clamp(to, min, max);
      if (f === t) return;
      const color = str(hop.color, ACCENTS[i % ACCENTS.length]);
      const x1 = xOf(f);
      const x2 = xOf(t);
      const baseY = y - 26;
      // Control point sits 2h above the ends so the visible apex is ~h.
      const h = clamp(Math.abs(x2 - x1) * 0.35, 44, 130);
      const mid = (x1 + x2) / 2;
      children.push(
        new Path(`M ${x1} ${baseY} Q ${mid} ${baseY - 2 * h} ${x2} ${baseY}`, {
          fill: '',
          stroke: color,
          strokeWidth: 5,
          strokeLineCap: 'round',
        }),
      );
      // Tangent at the landing end runs from the control point to the endpoint.
      const landAngle = Math.atan2(2 * h, x2 - mid);
      children.push(arrowHead(x2, baseY, landAngle, 11, color));
      const diff = Math.round((t - f) * 1000) / 1000;
      children.push(
        makeText(`${diff > 0 ? '+' : ''}${fmt(diff)}`, {
          x: mid,
          y: baseY - h - 12,
          size: numberSize * 0.85,
          font,
          fill: color,
          originY: 'bottom',
        }),
      );
    });

    return finish(numberLine, params, children);
  },
};

// ---------------------------------------------------------------------------
// 2. fraction-circles
// ---------------------------------------------------------------------------

const fractionCircles: ToolRecipe = {
  id: 'fraction-circles',
  domain: 'math',
  tags: ['fractions', 'model', 'circle', 'manipulative', 'parts-of-a-whole'],
  featured: true,
  name: 'Fraction Circle',
  category: 'math',
  version: 1,
  description:
    'Circle cut into equal wedges with the numerator portion filled — classic pie fraction model.',
  defaults: {
    numerator: 3,
    denominator: 4,
    radius: 140,
    filledColor: '#f97316',
    emptyColor: '#ffffff',
    strokeColor: INK,
    showLabel: true,
    fontFamily: 'Arial',
    labelFontSize: 56,
  },
  schema: [
    { key: 'numerator', label: 'Numerator', type: 'number', min: 0, step: 1 },
    { key: 'denominator', label: 'Denominator', type: 'number', min: 1, max: 36, step: 1 },
    { key: 'filledColor', label: 'Filled wedge color', type: 'fill' },
    { key: 'emptyColor', label: 'Empty wedge color', type: 'fill' },
    { key: 'strokeColor', label: 'Outline color', type: 'color' },
    { key: 'showLabel', label: 'Show n/d label', type: 'boolean' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'radius',
      label: 'Radius (px)',
      type: 'number',
      min: 40,
      max: 600,
      step: 5,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Label size',
      type: 'number',
      min: 16,
      max: 160,
      step: 2,
      advanced: true,
    },
  ],
  async generate(params) {
    const den = clamp(Math.round(num(params.denominator, 4)), 1, 36);
    const numer = clamp(Math.round(num(params.numerator, 3)), 0, den);
    const r = clamp(num(params.radius, 140), 20, 1000);
    // Fill params: a legacy solid string keeps the str() coercion (empty/junk →
    // default) so old designs render byte-identically; a gradient/texture
    // FillValue object passes through to applyFill unchanged.
    const filledFill: FillValue =
      typeof params.filledColor === 'string' || params.filledColor == null
        ? str(params.filledColor, '#f97316')
        : (params.filledColor as FillValue);
    const emptyFill: FillValue =
      typeof params.emptyColor === 'string' || params.emptyColor == null
        ? str(params.emptyColor, '#ffffff')
        : (params.emptyColor as FillValue);
    const stroke = str(params.strokeColor, INK);
    const font = str(params.fontFamily, 'Arial');
    const labelSize = clamp(num(params.labelFontSize, 56), 12, 200);
    const sw = clamp(r * 0.045, 4, 12);

    const cx = r;
    const cy = r;
    const children: FabricObject[] = [];
    // Each wedge / whole-circle paired with whether it wears the filled fill;
    // resolved once via applyFill after construction so gradient/texture fills
    // render (a solid string resolves to itself — byte-identical to the old
    // inline fill).
    const wedges: { obj: FabricObject; filled: boolean }[] = [];

    if (den === 1) {
      // A 1-wedge "pie" is just the whole circle — an SVG arc can't span 360°.
      const whole = new Circle({
        left: cx,
        top: cy,
        originX: 'center',
        originY: 'center',
        radius: r,
        stroke,
        strokeWidth: sw,
      });
      children.push(whole);
      wedges.push({ obj: whole, filled: numer >= 1 });
    } else {
      // Wedges start at 12 o'clock and sweep clockwise; each arc is <= 180°
      // (den >= 2), so the large-arc flag is always 0.
      for (let i = 0; i < den; i++) {
        const p0 = polar(cx, cy, r, (i / den) * TAU);
        const p1 = polar(cx, cy, r, ((i + 1) / den) * TAU);
        const wedge = new Path(
          `M ${cx} ${cy} L ${p0.x} ${p0.y} A ${r} ${r} 0 0 1 ${p1.x} ${p1.y} Z`,
          {
            stroke,
            strokeWidth: sw,
            strokeLineJoin: 'round',
          },
        );
        children.push(wedge);
        wedges.push({ obj: wedge, filled: i < numer });
      }
    }
    await Promise.all(
      wedges.map((w) =>
        applyFill(w.obj, w.filled ? filledFill : emptyFill, w.filled ? '#f97316' : '#ffffff'),
      ),
    );

    if (bool(params.showLabel, true)) {
      children.push(
        makeText(`${numer}/${den}`, {
          x: cx,
          y: cy + r + sw + 18,
          size: labelSize,
          font,
          originY: 'top',
        }),
      );
    }

    return finish(fractionCircles, params, children);
  },
};

// ---------------------------------------------------------------------------
// 3. fraction-bar
// ---------------------------------------------------------------------------

const fractionBar: ToolRecipe = {
  id: 'fraction-bar',
  domain: 'math',
  tags: ['fractions', 'model', 'tape-diagram', 'bar', 'manipulative'],
  name: 'Fraction Bar',
  category: 'math',
  version: 1,
  description:
    'Horizontal bar split into equal segments with the numerator portion filled — tape-diagram fraction model.',
  defaults: {
    numerator: 3,
    denominator: 4,
    width: 640,
    height: 110,
    filledColor: '#22c55e',
    emptyColor: '#ffffff',
    strokeColor: INK,
    showLabel: true,
    fontFamily: 'Arial',
    labelFontSize: 52,
  },
  schema: [
    { key: 'numerator', label: 'Numerator', type: 'number', min: 0, step: 1 },
    { key: 'denominator', label: 'Denominator', type: 'number', min: 1, max: 30, step: 1 },
    { key: 'filledColor', label: 'Filled segment color', type: 'fill' },
    { key: 'emptyColor', label: 'Empty segment color', type: 'fill' },
    { key: 'strokeColor', label: 'Outline color', type: 'color' },
    { key: 'showLabel', label: 'Show n/d label', type: 'boolean' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'width',
      label: 'Bar width (px)',
      type: 'number',
      min: 120,
      max: 2400,
      step: 10,
      advanced: true,
    },
    {
      key: 'height',
      label: 'Bar height (px)',
      type: 'number',
      min: 40,
      max: 400,
      step: 5,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Label size',
      type: 'number',
      min: 16,
      max: 160,
      step: 2,
      advanced: true,
    },
  ],
  async generate(params) {
    const den = clamp(Math.round(num(params.denominator, 4)), 1, 30);
    const numer = clamp(Math.round(num(params.numerator, 3)), 0, den);
    const width = clamp(num(params.width, 640), 100, 3000);
    const height = clamp(num(params.height, 110), 30, 600);
    // Fill params: a legacy solid string keeps the str() coercion (empty/junk →
    // default) so old designs render byte-identically; a gradient/texture
    // FillValue object passes through to applyFill unchanged.
    const filledFill: FillValue =
      typeof params.filledColor === 'string' || params.filledColor == null
        ? str(params.filledColor, '#22c55e')
        : (params.filledColor as FillValue);
    const emptyFill: FillValue =
      typeof params.emptyColor === 'string' || params.emptyColor == null
        ? str(params.emptyColor, '#ffffff')
        : (params.emptyColor as FillValue);
    const stroke = str(params.strokeColor, INK);
    const font = str(params.fontFamily, 'Arial');
    const labelSize = clamp(num(params.labelFontSize, 52), 12, 200);

    const children: FabricObject[] = [];
    const segW = width / den;
    // Each segment paired with whether it wears the filled fill; resolved once
    // via applyFill after construction (a solid string resolves to itself).
    const segments: { obj: FabricObject; filled: boolean }[] = [];
    for (let i = 0; i < den; i++) {
      const seg = new Rect({
        left: i * segW,
        top: 0,
        width: segW,
        height,
        stroke,
        strokeWidth: 6,
        strokeLineJoin: 'round',
      });
      children.push(seg);
      segments.push({ obj: seg, filled: i < numer });
    }
    await Promise.all(
      segments.map((s) =>
        applyFill(s.obj, s.filled ? filledFill : emptyFill, s.filled ? '#22c55e' : '#ffffff'),
      ),
    );

    if (bool(params.showLabel, true)) {
      children.push(
        makeText(`${numer}/${den}`, {
          x: width / 2,
          y: height + 20,
          size: labelSize,
          font,
          originY: 'top',
        }),
      );
    }

    return finish(fractionBar, params, children);
  },
};

// ---------------------------------------------------------------------------
// 4. ten-frame
// ---------------------------------------------------------------------------

const tenFrame: ToolRecipe = {
  id: 'ten-frame',
  domain: 'math',
  tags: ['ten-frame', 'counting', 'number-sense', 'subitizing', 'model', 'K-2'],
  name: 'Ten Frame',
  category: 'math',
  version: 1,
  description: 'Classic 5x2 ten frame holding 0-10 counters, filled top row first.',
  defaults: {
    count: 7,
    cellSize: 110,
    counterColor: '#ef4444',
    frameColor: INK,
  },
  schema: [
    { key: 'count', label: 'Counters (0-10)', type: 'number', min: 0, max: 10, step: 1 },
    { key: 'counterColor', label: 'Counter color', type: 'fill' },
    { key: 'frameColor', label: 'Frame color', type: 'color' },
    {
      key: 'cellSize',
      label: 'Cell size (px)',
      type: 'number',
      min: 40,
      max: 400,
      step: 5,
      advanced: true,
    },
  ],
  async generate(params) {
    const count = clamp(Math.round(num(params.count, 7)), 0, 10);
    const cell = clamp(num(params.cellSize, 110), 30, 600);
    // Counter fill: a legacy solid string keeps the str() coercion (empty/junk
    // → default) so old designs render byte-identically; a gradient/texture
    // FillValue passes through to applyFill.
    const counterFill: FillValue =
      typeof params.counterColor === 'string' || params.counterColor == null
        ? str(params.counterColor, '#ef4444')
        : (params.counterColor as FillValue);
    const frameColor = str(params.frameColor, INK);

    const children: FabricObject[] = [];

    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 5; col++) {
        children.push(
          new Rect({
            left: col * cell,
            top: row * cell,
            width: cell,
            height: cell,
            fill: '#ffffff',
            stroke: frameColor,
            strokeWidth: clamp(cell * 0.045, 3, 8),
          }),
        );
      }
    }
    // Heavier outer border over the cell grid reads as one chunky frame.
    children.push(
      new Rect({
        left: 0,
        top: 0,
        width: cell * 5,
        height: cell * 2,
        fill: '',
        stroke: frameColor,
        strokeWidth: clamp(cell * 0.08, 5, 14),
        strokeLineJoin: 'round',
      }),
    );

    // Counters fill the top row left-to-right, then the bottom row.
    const counters: FabricObject[] = [];
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / 5);
      const col = i % 5;
      const counter = new Circle({
        left: (col + 0.5) * cell,
        top: (row + 0.5) * cell,
        originX: 'center',
        originY: 'center',
        radius: cell * 0.34,
        stroke: frameColor,
        strokeWidth: clamp(cell * 0.03, 2, 6),
      });
      children.push(counter);
      counters.push(counter);
    }
    // Resolve the counter fill once per counter (a solid string → itself).
    await Promise.all(counters.map((c) => applyFill(c, counterFill, '#ef4444')));

    return finish(tenFrame, params, children);
  },
};

// ---------------------------------------------------------------------------
// 5. analog-clock
// ---------------------------------------------------------------------------

const analogClock: ToolRecipe = {
  id: 'analog-clock',
  domain: 'math',
  tags: ['clock', 'time', 'telling-time', 'measurement', 'K-2'],
  name: 'Analog Clock',
  category: 'math',
  version: 1,
  description:
    'Teaching clock showing a given time: bold rim, numerals 1-12, hour/minute ticks, chunky rounded hands.',
  defaults: {
    hour: 3,
    minute: 0,
    radius: 180,
    faceColor: '#ffffff',
    rimColor: '#2563eb',
    tickColor: INK,
    hourHandColor: INK,
    minuteHandColor: '#dc2626',
    showMinuteTicks: true,
    fontFamily: 'Arial',
    labelFontSize: 46,
  },
  schema: [
    { key: 'hour', label: 'Hour (1-12)', type: 'number', min: 1, max: 12, step: 1 },
    { key: 'minute', label: 'Minute (0-59)', type: 'number', min: 0, max: 59, step: 1 },
    { key: 'faceColor', label: 'Face fill', type: 'fill' },
    { key: 'rimColor', label: 'Rim color', type: 'color' },
    { key: 'tickColor', label: 'Tick/numeral color', type: 'color' },
    { key: 'hourHandColor', label: 'Hour hand color', type: 'color' },
    { key: 'minuteHandColor', label: 'Minute hand color', type: 'color' },
    { key: 'showMinuteTicks', label: 'Show minute ticks', type: 'boolean' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'radius',
      label: 'Radius (px)',
      type: 'number',
      min: 60,
      max: 800,
      step: 10,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Numeral size',
      type: 'number',
      min: 14,
      max: 160,
      step: 2,
      advanced: true,
    },
  ],
  async generate(params) {
    const hour = ((Math.round(num(params.hour, 3)) % 12) + 12) % 12;
    const minute = clamp(Math.round(num(params.minute, 0)), 0, 59);
    const r = clamp(num(params.radius, 180), 50, 1200);
    // Strings keep the legacy str() coercion (empty/junk → white) so old
    // solid-color params render byte-identically; objects go through applyFill.
    const faceFill: FillValue =
      typeof params.faceColor === 'string' || params.faceColor == null
        ? str(params.faceColor, '#ffffff')
        : (params.faceColor as FillValue);
    const rimColor = str(params.rimColor, '#2563eb');
    const tickColor = str(params.tickColor, INK);
    const hourHandColor = str(params.hourHandColor, INK);
    const minuteHandColor = str(params.minuteHandColor, '#dc2626');
    const font = str(params.fontFamily, 'Arial');
    // Numeral size tracks the face unless the caller overrides it.
    const numeralSize = clamp(num(params.labelFontSize, r * 0.26), 10, r * 0.5);

    const cx = r;
    const cy = r;
    const children: FabricObject[] = [];

    const face = new Circle({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      radius: r,
      fill: '#ffffff',
      stroke: rimColor,
      strokeWidth: clamp(r * 0.09, 8, 26),
    });
    await applyFill(face, faceFill, '#ffffff');
    children.push(face);

    // Minute ticks first so hour ticks paint over them at the multiples of 5.
    if (bool(params.showMinuteTicks, true)) {
      for (let m = 0; m < 60; m++) {
        if (m % 5 === 0) continue;
        const a = (m / 60) * TAU;
        const p1 = polar(cx, cy, r * 0.88, a);
        const p2 = polar(cx, cy, r * 0.83, a);
        children.push(strokeLine(p1.x, p1.y, p2.x, p2.y, tickColor, clamp(r * 0.012, 1.5, 4)));
      }
    }
    // Hour ticks, extra bold at 12/3/6/9.
    for (let h = 0; h < 12; h++) {
      const a = (h / 12) * TAU;
      const quarter = h % 3 === 0;
      const p1 = polar(cx, cy, r * 0.88, a);
      const p2 = polar(cx, cy, r * (quarter ? 0.72 : 0.77), a);
      children.push(
        strokeLine(p1.x, p1.y, p2.x, p2.y, tickColor, clamp(r * (quarter ? 0.05 : 0.03), 3, 16)),
      );
    }
    // Numerals sit inside the tick ring.
    for (let n = 1; n <= 12; n++) {
      const p = polar(cx, cy, r * 0.58, (n / 12) * TAU);
      children.push(
        makeText(String(n), { x: p.x, y: p.y, size: numeralSize, font, fill: tickColor }),
      );
    }

    // Hands: angles measured clockwise from 12; the hour hand advances with
    // the minutes so 3:30 reads halfway between 3 and 4. Short tails behind
    // the pivot make the hands look balanced.
    const minuteAngle = (minute / 60) * TAU;
    const hourAngle = ((hour + minute / 60) / 12) * TAU;
    const hourTip = polar(cx, cy, r * 0.5, hourAngle);
    const hourTail = polar(cx, cy, r * 0.12, hourAngle + Math.PI);
    children.push(
      strokeLine(hourTail.x, hourTail.y, hourTip.x, hourTip.y, hourHandColor, clamp(r * 0.075, 6, 22)),
    );
    const minTip = polar(cx, cy, r * 0.74, minuteAngle);
    const minTail = polar(cx, cy, r * 0.14, minuteAngle + Math.PI);
    children.push(
      strokeLine(minTail.x, minTail.y, minTip.x, minTip.y, minuteHandColor, clamp(r * 0.05, 4, 16)),
    );
    children.push(
      new Circle({
        left: cx,
        top: cy,
        originX: 'center',
        originY: 'center',
        radius: clamp(r * 0.055, 5, 20),
        fill: tickColor,
      }),
    );

    return finish(analogClock, params, children);
  },
};

// ---------------------------------------------------------------------------
// 6. coordinate-grid
// ---------------------------------------------------------------------------

interface PointSpec {
  x?: unknown;
  y?: unknown;
  label?: unknown;
  color?: unknown;
}

const coordinateGrid: ToolRecipe = {
  id: 'coordinate-grid',
  domain: 'math',
  tags: ['coordinate-grid', 'graphing', 'plotting', 'geometry', 'points', 'quadrant'],
  name: 'Coordinate Grid',
  category: 'math',
  version: 1,
  description:
    'First-quadrant coordinate grid with bold arrowed axes, axis numbers, and optional labelled plotted points.',
  defaults: {
    xMax: 10,
    yMax: 10,
    cellSize: 56,
    gridColor: '#cbd5e1',
    axisColor: INK,
    fontFamily: 'Arial',
    labelFontSize: 24,
    points: [],
  },
  schema: [
    { key: 'xMax', label: 'X max', type: 'number', min: 1, max: 30, step: 1 },
    { key: 'yMax', label: 'Y max', type: 'number', min: 1, max: 30, step: 1 },
    {
      key: 'points',
      label: 'Plotted points',
      type: 'list',
      help: 'Dots plotted on the grid, each with an optional letter/name label.',
      summaryKeys: ['label', 'x', 'y'],
      minItems: 0,
      maxItems: 20,
      itemSchema: [
        { key: 'x', label: 'X', type: 'number', min: 0, step: 0.5 },
        { key: 'y', label: 'Y', type: 'number', min: 0, step: 0.5 },
        { key: 'label', label: 'Label', type: 'text', help: 'Optional, e.g. "A".' },
        {
          key: 'color',
          label: 'Point color',
          type: 'color',
          help: 'Leave unset to auto-pick an accent color.',
        },
      ],
    },
    { key: 'gridColor', label: 'Gridline color', type: 'color' },
    { key: 'axisColor', label: 'Axis color', type: 'color' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'cellSize',
      label: 'Cell size (px)',
      type: 'number',
      min: 20,
      max: 200,
      step: 2,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Number size',
      type: 'number',
      min: 10,
      max: 80,
      step: 1,
      advanced: true,
    },
  ],
  async generate(params) {
    const xMax = clamp(Math.round(num(params.xMax, 10)), 1, 40);
    const yMax = clamp(Math.round(num(params.yMax, 10)), 1, 40);
    const cell = clamp(num(params.cellSize, 56), 16, 300);
    const gridColor = str(params.gridColor, '#cbd5e1');
    const axisColor = str(params.axisColor, INK);
    const font = str(params.fontFamily, 'Arial');
    const numberSize = clamp(num(params.labelFontSize, 24), 8, 100);

    // Origin in the lower-left; canvas y grows downward, so plotting (x, v)
    // maps to pixel y = oy - v * cell.
    const ox = 60;
    const oy = 40 + yMax * cell;
    const children: FabricObject[] = [];

    // Interior gridlines (the axes overdraw the i = 0 lines).
    for (let i = 1; i <= xMax; i++) {
      children.push(strokeLine(ox + i * cell, oy, ox + i * cell, oy - yMax * cell, gridColor, 2));
    }
    for (let i = 1; i <= yMax; i++) {
      children.push(strokeLine(ox, oy - i * cell, ox + xMax * cell, oy - i * cell, gridColor, 2));
    }

    // Bold axes with arrowheads pointing right and up.
    const xEnd = ox + xMax * cell + 30;
    const yEnd = oy - yMax * cell - 30;
    children.push(strokeLine(ox, oy, xEnd, oy, axisColor, 6));
    children.push(strokeLine(ox, oy, ox, yEnd, axisColor, 6));
    children.push(arrowHead(xEnd + 18, oy, 0, 13, axisColor));
    children.push(arrowHead(ox, yEnd - 18, -Math.PI / 2, 13, axisColor));

    // Axis numbers — thin the labels when the range would crowd them.
    const labelStep = xMax > 20 || yMax > 20 ? 5 : xMax > 12 || yMax > 12 ? 2 : 1;
    for (let i = labelStep; i <= xMax; i += labelStep) {
      children.push(
        makeText(String(i), {
          x: ox + i * cell,
          y: oy + 12,
          size: numberSize,
          font,
          fill: axisColor,
          originY: 'top',
        }),
      );
    }
    for (let i = labelStep; i <= yMax; i += labelStep) {
      children.push(
        makeText(String(i), {
          x: ox - 14,
          y: oy - i * cell,
          size: numberSize,
          font,
          fill: axisColor,
          originX: 'right',
        }),
      );
    }
    children.push(
      makeText('0', {
        x: ox - 14,
        y: oy + 12,
        size: numberSize,
        font,
        fill: axisColor,
        originX: 'right',
        originY: 'top',
      }),
    );

    const points = jsonArray<PointSpec>(params.points, []);
    points.forEach((pt, i) => {
      if (!pt || typeof pt !== 'object') return;
      const px = num(pt.x, NaN);
      const py = num(pt.y, NaN);
      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      const gx = ox + clamp(px, 0, xMax) * cell;
      const gy = oy - clamp(py, 0, yMax) * cell;
      const color = str(pt.color, ACCENTS[i % ACCENTS.length]);
      children.push(
        new Circle({
          left: gx,
          top: gy,
          originX: 'center',
          originY: 'center',
          radius: clamp(cell * 0.17, 6, 16),
          fill: color,
          stroke: '#ffffff',
          strokeWidth: 3,
        }),
      );
      const label = str(pt.label, '');
      if (label) {
        children.push(
          makeText(label, {
            x: gx + 14,
            y: gy - 12,
            size: numberSize * 1.15,
            font,
            fill: color,
            originX: 'left',
            originY: 'bottom',
          }),
        );
      }
    });

    return finish(coordinateGrid, params, children);
  },
};

// ---------------------------------------------------------------------------
// 7. timeline
// ---------------------------------------------------------------------------

interface EventSpec {
  label?: unknown;
  sublabel?: unknown;
}

const SAMPLE_EVENTS: EventSpec[] = [
  { label: '1969', sublabel: 'Moon landing' },
  { label: '1990', sublabel: 'Hubble launches' },
  { label: '2012', sublabel: 'Curiosity on Mars' },
  { label: '2021', sublabel: 'Webb telescope' },
];

const timeline: ToolRecipe = {
  id: 'timeline',
  domain: 'social',
  tags: ['timeline', 'sequence', 'history', 'events', 'chronology', 'plot'],
  name: 'Timeline',
  category: 'math',
  version: 1,
  description:
    'Horizontal arrowed timeline with evenly spaced events on alternating above/below stems.',
  defaults: {
    width: 1000,
    lineColor: INK,
    fontFamily: 'Arial',
    labelFontSize: 28,
    events: SAMPLE_EVENTS.map((e) => ({ ...e })),
  },
  schema: [
    {
      key: 'events',
      label: 'Events',
      type: 'list',
      help: 'Shown left to right in this order, on alternating sides of the line.',
      summaryKeys: ['label', 'sublabel'],
      minItems: 1,
      maxItems: 24,
      itemSchema: [
        { key: 'label', label: 'Title', type: 'text', help: 'Usually the year or date.' },
        { key: 'sublabel', label: 'Caption', type: 'text', help: 'Optional smaller line.' },
      ],
    },
    { key: 'lineColor', label: 'Line color', type: 'color' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'width',
      label: 'Width (px)',
      type: 'number',
      min: 300,
      max: 4000,
      step: 10,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Label size',
      type: 'number',
      min: 12,
      max: 100,
      step: 1,
      advanced: true,
    },
  ],
  async generate(params) {
    const width = clamp(num(params.width, 1000), 300, 5000);
    const lineColor = str(params.lineColor, INK);
    const font = str(params.fontFamily, 'Arial');
    const labelSize = clamp(num(params.labelFontSize, 28), 10, 120);
    const subSize = labelSize * 0.72;

    // An empty timeline is useless on a poster — fall back to a sample set.
    let events = jsonArray<EventSpec>(params.events, SAMPLE_EVENTS)
      .filter((e): e is EventSpec => Boolean(e) && typeof e === 'object')
      .slice(0, 24);
    if (events.length === 0) events = SAMPLE_EVENTS;

    const y = 300; // room for stems both sides; group bbox trims
    const children: FabricObject[] = [];

    children.push(strokeLine(24, y, width - 46, y, lineColor, 8));
    children.push(arrowHead(width - 24, y, 0, 16, lineColor));

    const n = events.length;
    const innerL = 84;
    const innerR = width - 110; // keep the last event clear of the arrowhead
    const xAt = (i: number) => (n === 1 ? (innerL + innerR) / 2 : innerL + (i * (innerR - innerL)) / (n - 1));
    const stemLen = 78;

    events.forEach((event, i) => {
      const x = xAt(i);
      const up = i % 2 === 0;
      const dir = up ? -1 : 1;
      const accent = ACCENTS[i % ACCENTS.length];
      const stemEnd = y + dir * (16 + stemLen);

      children.push(strokeLine(x, y + dir * 16, x, stemEnd, accent, 5));
      children.push(
        new Circle({
          left: x,
          top: y,
          originX: 'center',
          originY: 'center',
          radius: 13,
          fill: accent,
          stroke: '#ffffff',
          strokeWidth: 4,
        }),
      );

      const label = str(event.label, `Event ${i + 1}`);
      const sublabel = str(event.sublabel, '');
      if (up) {
        // Stack grows upward from the stem tip: sublabel nearest, label above.
        if (sublabel) {
          children.push(
            makeText(sublabel, {
              x,
              y: stemEnd - 8,
              size: subSize,
              font,
              fill: '#64748b',
              weight: 'normal',
              originY: 'bottom',
            }),
          );
        }
        children.push(
          makeText(label, {
            x,
            y: stemEnd - 8 - (sublabel ? subSize * 1.3 : 0),
            size: labelSize,
            font,
            fill: lineColor,
            originY: 'bottom',
          }),
        );
      } else {
        children.push(
          makeText(label, {
            x,
            y: stemEnd + 8,
            size: labelSize,
            font,
            fill: lineColor,
            originY: 'top',
          }),
        );
        if (sublabel) {
          children.push(
            makeText(sublabel, {
              x,
              y: stemEnd + 8 + labelSize * 1.3,
              size: subSize,
              font,
              fill: '#64748b',
              weight: 'normal',
              originY: 'top',
            }),
          );
        }
      }
    });

    return finish(timeline, params, children);
  },
};

registerRecipe(numberLine);
registerRecipe(fractionCircles);
registerRecipe(fractionBar);
registerRecipe(tenFrame);
registerRecipe(analogClock);
registerRecipe(coordinateGrid);
registerRecipe(timeline);
